import { selectStandardProps } from "@/utils/componentUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Card, Flex, Form, Input, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import ActionButton from "../../../../common/components/buttons/ActionButton";
import AntIcon from "../../../../common/components/icons/AntIcon";
import { rowGutter } from "../../../../common/constants";
import { InstitutionTreeSelect } from "../../../enumerations/components/form/InstitutionTreeSelect";
import ProductGroupSelect from "../../../enumerations/components/form/ProductGroupSelect";
import { DEFAULT_FILTER_PRODUCTS_QUERY_PARAMS } from "../../api";
import { ProductFinancialSector } from "../../enums";
import { ProductFilterPageRequest } from "../../types";

type Props = {
  onCreateClick: () => void;
};

export const ProductFilterView = ({ onCreateClick }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<ProductFilterPageRequest>();

  const navigate = useNavigate();
  const searchFilterParams = useFilterSearchParams<ProductFilterPageRequest>({
    params: ["sectors", "groupIds", "institutionIds"],
    defaultQueryParamsObject: DEFAULT_FILTER_PRODUCTS_QUERY_PARAMS,
  });

  useEffect(() => {
    form.setFieldsValue({
      keyword: searchFilterParams.keyword,
      groupIds: searchFilterParams.groupIds,
      sectors: searchFilterParams.sectors,
      institutionIds: searchFilterParams.institutionIds,
    });
  }, [searchFilterParams]);

  const handleSearchSubmit = (values: ProductFilterPageRequest) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            keyword: values.keyword,
            sectors: values.sectors,
            groupIds: values.groupIds,
            institutionIds: values.institutionIds,
            pageIndex: undefined,
          },
          append: true,
        }),
      },
      { replace: true },
    );
  };

  const handleFilterClear = () => {
    form.resetFields();
    handleSearchSubmit(form.getFieldsValue());
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("product.titles.productsAdmin")}</h2>}
      extra={
        <>
          <Button type="primary" icon={<AntIcon type="plus" />} onClick={onCreateClick}>
            {t("product.actions.createProduct")}
          </Button>

          <ActionButton color="danger" size="middle" variant="outlined" icon="reload" onClick={handleFilterClear}>
            {t("common.resetFilter")}
          </ActionButton>
        </>
      }
    >
      <Form form={form} onFinish={handleSearchSubmit} layout="vertical" name="productSearchForm">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: rowGutter,
            alignItems: "end",
          }}
          className="min-width-0-children"
        >
          <Form.Item
            name="keyword"
            label={t("common.searchKeyword")}
            rules={[
              validations.size(
                validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
                validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
              ),
            ]}
          >
            <Input allowClear placeholder={t("product.helpers.productSearchHint")} />
          </Form.Item>

          <ProductGroupSelect
            formItemProps={{
              name: "groupIds",
              label: t("product.attrs.productGroupId"),
              rules: [validations.none],
            }}
            selectProps={{
              mode: "multiple",
              maxTagCount: "responsive",
              allowClear: true,
            }}
          />

          <Form.Item name="sectors" label={t("product.enums.financialSector._label")} rules={[validations.none]}>
            <Select
              {...selectStandardProps}
              allowClear
              mode="multiple"
              maxTagCount="responsive"
              options={Object.keys(ProductFinancialSector)
                .filter((sector) => sector !== ProductFinancialSector.ALL)
                .map((sector) => ({
                  value: sector,
                  label: t("product.enums.financialSector." + sector),
                }))}
            />
          </Form.Item>

          <Form.Item name="institutionIds" label={t("product.attrs.institutionIds")} rules={[validations.none]}>
            <InstitutionTreeSelect />
          </Form.Item>
        </div>

        <Flex justify="end">
          <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
            {t("common.filter")}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
};
